import {
  type WindowLocation,
  useLocation as useLocationReachRouter
} from '@reach/router'
import {
  type Location,
  useLocation as useLocationReactRouter
} from 'react-router'

// useLocation returns a different type depending on the router used. But we only use a few properties from the location object.
// so we are definig a type that includes only the properties we use to make sure the api stays the same for existing components
export type LocationType = {
  readonly pathname: string
  readonly search: string
  readonly href: string
  readonly key?: string
  readonly hash?: string
  readonly state?: unknown
}

/**
 * Returns a location object that is compliant with LocationType,
 * abstracting away the specific router being used.
 *
 * This works with both Reach Router and React Router.
 *
 * It will try to use Reach Router's useLocation first,
 * and if that fails, it will fall back to React Router's useLocation.
 *
 * This 100% breaks rules of hooks, so it is a temporary solution
 * until we can refactor the code to use a single router. (ECP-14108)
 */
export const useLocation = (): LocationType => {
  try {
    const location: WindowLocation = useLocationReachRouter()
    return {
      href: location.href,
      pathname: location.pathname,
      search: location.search,
      key: location.key,
      hash: location.hash,
      state: location.state
    }
  } catch {
    const location: Location = useLocationReactRouter()
    return {
      href: `${globalThis.window?.location.origin}${location.pathname}${location.search}`, // this is not a defined property in React Router's Location type, and we are using it on components, so we are constructing it manually; We must either refactor components to not use href, or keep it as a custom property
      pathname: location.pathname,
      search: location.search,
      key: location.key,
      hash: location.hash,
      state: location.state
    }
  }
}
